import ChecklistComplementItemsGroundList from '@/components/Movements/WorkOrders/WorkorderChecklist/ChecklistComplementItems/ComplementItemsGroundList';
import ChecklistComplementItemsLiftList from '@/components/Movements/WorkOrders/WorkorderChecklist/ChecklistComplementItems/ComplementItemsLiftList';
import ChecklistComplementItemsTestRunList from '@/components/Movements/WorkOrders/WorkorderChecklist/ChecklistComplementItems/ComplementItemsTestRunList';
import PortoComplementItems from '@/components/Movements/WorkOrders/WorkorderChecklist/ChecklistComplementItems/PortoComplementItems';
import WorkorderChecklistFooter from '@/components/Movements/WorkOrders/WorkorderChecklist/ChecklistFooter';
import { useWorkorderChecklistContext } from '@/components/Movements/WorkOrders/WorkorderChecklist/WorkorderChecklistProvider';
import { WorkorderChecklistItemType } from '@/components/Movements/WorkOrders/WorkorderChecklist/WorkorderChecklistProvider/WorkorderChecklistProvider.types';
import { DefaultRouteTabs } from '@/components/_common';
import {
  ChecklistComplementaryItemPosition,
  checklistComplementaryItems,
} from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.ComplementaryItemsConstant';
import { checklistPortoComplementaryItems } from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.PortoComplementaryItemsConstant';
import {
  useWorkOrderChecklistComplementItemCreate,
  useWorkOrderChecklistComplementItemList,
} from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.hooks';
import {
  ChecklistComplementItemFields,
  ChecklistComplementItemFormValues,
  ChecklistComplementItemResponse,
} from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.types';
import useChecklistValidRoutes from '@/hooks/useChecklistValidRoutes';
import { isAppPortocar } from '@/index';
import { PageProps } from '@/types/PageProps';
import { Button, Divider, Form } from 'antd';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';

const WorkorderChecklistComplementItemsPage: React.FC<PageProps> = ({
  route,
}) => {
  const intl = useIntl();
  const { checklistItemId, checklistItemType, workorderId, boschChecklist } =
    useWorkorderChecklistContext();
  const [form] = Form.useForm<ChecklistComplementItemFormValues>();

  const complementItemCreate = useWorkOrderChecklistComplementItemCreate();
  const complementItemList = useWorkOrderChecklistComplementItemList({
    diagnosticId:
      checklistItemType === WorkorderChecklistItemType.Diagnostic
        ? checklistItemId
        : undefined,
    workOrderComplementId:
      checklistItemType === WorkorderChecklistItemType.Workorder
        ? checklistItemId
        : undefined,
  });

  const routeParent = useChecklistValidRoutes({
    route,
    checklistItemType,
    workorderId,
    boschChecklist,
  });

  useEffect(() => {
    if (complementItemList.data?.length) {
      if (isAppPortocar) {
        getPortoItemsFields();
      } else {
        form.setFieldsValue({
          'items-ground': getItemsFields(
            ChecklistComplementaryItemPosition.Ground,
          ).map((item) => {
            return { ...item, response: [item.response] };
          }),
        });

        form.setFieldValue(
          'items-lift',
          getItemsFields(ChecklistComplementaryItemPosition.Lift).map(
            (item) => {
              return { ...item, response: [item.response] };
            },
          ),
        );

        form.setFieldValue(
          'items-test-run',
          getItemsFields(ChecklistComplementaryItemPosition.TestRun).map(
            (item) => {
              return { ...item, response: [item.response] };
            },
          ),
        );
      }
    }
  }, [complementItemList.dataUpdatedAt]);

  function getItemsFields(positionType: ChecklistComplementaryItemPosition) {
    return checklistComplementaryItems
      .filter((item) => item.position === positionType)
      .map<unknown>((item) => {
        const itemFinded = complementItemList.data?.find(
          (dataItem) => dataItem.description === item.value,
        );
        if (itemFinded) {
          return { ...item, ...itemFinded };
        }
        return {
          ...item,
          response: undefined,
          comment: undefined,
          description: item.value,
        };
      }) as ChecklistComplementItemFields[];
  }

  function getPortoItemsFields() {
    const dataMapped = checklistPortoComplementaryItems.map((category) => {
      const data = category.items.map((item) => {
        const itemFinded = complementItemList.data?.find(
          (dataItem) => dataItem.description === item.value,
        );

        if (itemFinded) {
          return { ...item, ...itemFinded, response: [itemFinded.response] };
        }

        return {
          ...item,
          response: undefined,
          comment: undefined,
          description: item.value,
        };
      });

      return { [category.key]: data };
    });

    const formValues = dataMapped.reduce((acc, curr) => {
      const key = Object.keys(curr)[0];
      acc[key] = curr[key];
      return acc;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }, {} as Record<string, any>);

    form.setFieldsValue(formValues);
  }

  async function handleSubmit() {
    try {
      const values = await form.validateFields();

      let answers;

      if (isAppPortocar) {
        answers = Object.values(values)
          .flatMap((items) => items)
          .filter((item) => item.response && item.response[0])
          .map((item) => {
            return {
              description: item.description,
              comment: item.comment,
              response: item.response[0] as ChecklistComplementItemResponse,
            };
          });
      } else {
        answers = [
          ...values['items-ground']
            .filter((item) => item.response && item.response[0])
            .map((item) => {
              return {
                description: item.description,
                comment: item.comment,
                response: item.response[0] as ChecklistComplementItemResponse,
              };
            }),
          ...values['items-lift']
            .filter((item) => item.response && item.response[0])
            .map((item) => {
              return {
                description: item.description,
                comment: item.comment,
                response: item.response[0] as ChecklistComplementItemResponse,
              };
            }),
          ...values['items-test-run']
            .filter((item) => item.response && item.response[0])
            .map((item) => {
              return {
                description: item.description,
                comment: item.comment,
                response: item.response[0] as ChecklistComplementItemResponse,
              };
            }),
        ];
      }

      if (checklistItemType === WorkorderChecklistItemType.Diagnostic) {
        await complementItemCreate.mutateAsync({
          diagnosticId: checklistItemId,
          answers,
        });
      } else if (checklistItemType === WorkorderChecklistItemType.Workorder) {
        await complementItemCreate.mutateAsync({
          workOrderComplementId: checklistItemId,
          answers,
        });
      }
    } catch {}
  }

  return (
    <DefaultRouteTabs
      route={routeParent}
      baseKey={String(checklistItemId)}
      basePath={`/app/movements/workorderChecklist/${checklistItemId}`}
      extra={
        <Button
          block
          type="primary"
          onClick={handleSubmit}
          loading={complementItemCreate.isLoading}
        >
          Salvar Complementos
        </Button>
      }
    >
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <Form form={form}>
        {isAppPortocar && <PortoComplementItems />}

        {!isAppPortocar && (
          <>
            <ChecklistComplementItemsGroundList />

            <Divider />

            <ChecklistComplementItemsLiftList />

            <Divider />

            <ChecklistComplementItemsTestRunList />
          </>
        )}
      </Form>

      <WorkorderChecklistFooter />
    </DefaultRouteTabs>
  );
};

export default WorkorderChecklistComplementItemsPage;
